import React from 'react';
import { Button, Dialog, Flex, Text, TextInput } from '@applyboard/crystal-ui';

export type AddCourseState = {
  courseCode: string;
  courseName: string;
  marksObtained: string;
  grade: string;
  credits: string;
  semester: string;
};

export type AddCourseErrors = Partial<Record<keyof AddCourseState, string>>;

type AddCourseDialogProps = {
  open: boolean;
  isSaving: boolean;
  values: AddCourseState;
  errors: AddCourseErrors;
  onChange: (field: keyof AddCourseState, value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
};

export function AddCourseDialog({
  open,
  isSaving,
  values,
  errors,
  onChange,
  onClose,
  onSubmit,
}: Readonly<AddCourseDialogProps>) {
  return (
    <Dialog open={open} onOpenChange={(dialogOpen) => (!dialogOpen ? onClose() : null)} size="lg">
      <Dialog.Heading>Add New Course</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={3}>
          <Text variant="bodyM" intent="secondary">
            Source Information (from transcript)
          </Text>
          <Flex gap={3} wrap>
            <div style={{ minWidth: 220, flex: 1 }}>
              <Flex direction="column" gap={1}>
                <Text variant="labelM">Course Name *</Text>
                <TextInput
                  placeholder="e.g., Calculus I"
                  value={values.courseName}
                  onChange={(value) => onChange('courseName', value)}
                  aria-label="Course Name"
                  invalid={!!errors.courseName}
                  disabled={isSaving}
                />
                {errors.courseName && (
                  <Text variant="bodyS" intent="negative">
                    {errors.courseName}
                  </Text>
                )}
              </Flex>
            </div>
            <div style={{ minWidth: 220, flex: 1 }}>
              <Flex direction="column" gap={1}>
                <Text variant="labelM">Course Code</Text>
                <TextInput
                  placeholder="e.g. MATH"
                  value={values.courseCode}
                  onChange={(value) => onChange('courseCode', value)}
                  aria-label="Course Code"
                  invalid={!!errors.courseCode}
                  disabled={isSaving}
                />
                {errors.courseCode && (
                  <Text variant="bodyS" intent="negative">
                    {errors.courseCode}
                  </Text>
                )}
              </Flex>
            </div>
          </Flex>
          <Flex gap={3} wrap>
            <div style={{ minWidth: 200, flex: 1 }}>
              <Flex direction="column" gap={1}>
                <Text variant="labelM">Marks Obtained</Text>
                <TextInput
                  placeholder="e.g., 85"
                  value={values.marksObtained}
                  onChange={(value) => onChange('marksObtained', value)}
                  aria-label="Marks Obtained"
                  invalid={!!errors.marksObtained}
                  disabled={isSaving}
                />
                {errors.marksObtained && (
                  <Text variant="bodyS" intent="negative">
                    {errors.marksObtained}
                  </Text>
                )}
              </Flex>
            </div>
            <div style={{ minWidth: 200, flex: 1 }}>
              <Flex direction="column" gap={1}>
                <Text variant="labelM">Grade</Text>
                <TextInput
                  placeholder="e.g., A, B+"
                  value={values.grade}
                  onChange={(value) => onChange('grade', value)}
                  aria-label="Grade"
                  invalid={!!errors.grade}
                  disabled={isSaving}
                />
                {errors.grade && (
                  <Text variant="bodyS" intent="negative">
                    {errors.grade}
                  </Text>
                )}
              </Flex>
            </div>
            <div style={{ minWidth: 200, flex: 1 }}>
              <Flex direction="column" gap={1}>
                <Text variant="labelM">Credits</Text>
                <TextInput
                  placeholder="e.g., 3.0"
                  value={values.credits}
                  onChange={(value) => onChange('credits', value)}
                  aria-label="Credits"
                  invalid={!!errors.credits}
                  disabled={isSaving}
                />
                {errors.credits && (
                  <Text variant="bodyS" intent="negative">
                    {errors.credits}
                  </Text>
                )}
              </Flex>
            </div>
          </Flex>
          <div style={{ minWidth: 200, flex: 1 }}>
            <Flex direction="column" gap={1}>
              <Text variant="labelM">Semester</Text>
              <TextInput
                placeholder="e.g., Fall 2024"
                value={values.semester}
                onChange={(value) => onChange('semester', value)}
                aria-label="Semester"
                invalid={!!errors.semester}
                disabled={isSaving}
              />
              {errors.semester && (
                <Text variant="bodyS" intent="negative">
                  {errors.semester}
                </Text>
              )}
            </Flex>
          </div>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Button width="fill" intent="secondary" emphasis="outlined" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          width="fill"
          intent="primary"
          emphasis="contained"
          onClick={onSubmit}
          disabled={isSaving}
          loading={isSaving}
        >
          Add course to table
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
